import { presetForDeliverable } from '@/pages/collabs/brief-templates/editor/sections/deliverable-presets';
import { ugcPresetForDeliverable } from '@/pages/collabs/brief-templates/editor/sections/ugc-deliverable-presets';
import {
  briefGiftChoiceLabel,
  type BriefDeliverable,
  type BriefDeliverablePlatform,
  type BriefTemplateContent,
} from '@fluenceur/types';
import type { TFunction } from 'i18next';
import { formatCents } from './format-money';

export const PLATFORM_LABEL: Record<BriefDeliverablePlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
};

/** What an empty cell or value reads as wherever a brief is summarized. */
export const EMPTY_VALUE = '--';

/** A deliverable's format in words: the preset's label, else the stored format. */
export const deliverableLabel = (deliverable: BriefDeliverable, t: TFunction): string => {
  const ugcPreset = ugcPresetForDeliverable(deliverable);
  if (ugcPreset) return t(`collabs.briefTemplates.sections.deliverables.formats.${ugcPreset.key}`);
  const preset = presetForDeliverable(deliverable);
  return preset
    ? t(`collabs.briefTemplates.sections.deliverables.formats.${preset.key}`)
    : deliverable.format;
};

/**
 * What qualifies a deliverable beside its format: the platform it is published
 * on, or, for a UGC video, how long it may run. Null for a UGC photo.
 */
export const deliverableSpec = (deliverable: BriefDeliverable, t: TFunction): string | null => {
  if (deliverable.platform) return PLATFORM_LABEL[deliverable.platform];
  if (deliverable.maxDurationSeconds) {
    return t('collabs.briefTemplates.sections.deliverables.maxDurationShort', {
      seconds: deliverable.maxDurationSeconds,
    });
  }
  return null;
};

/**
 * "2 × Reel (Instagram) · 1 × Video (TikTok)", or "3 × Video 9:16 (30 s max)"
 * on a UGC brief: what a brief asks for, in one line.
 */
export const briefDeliverablesLine = (content: BriefTemplateContent, t: TFunction): string =>
  content.deliverables
    .map((deliverable) => {
      const spec = deliverableSpec(deliverable, t);
      const line = `${deliverable.quantity} × ${deliverableLabel(deliverable, t)}`;
      return spec ? `${line} (${spec})` : line;
    })
    .join(' · ');

/** What a brief pays: an amount, a product, or nothing. */
export const briefCompensationLine = (
  content: BriefTemplateContent,
  t: TFunction,
  language: string
): string => {
  const { compensation } = content;
  if (compensation.type === 'fixed_payment' && compensation.fixedPayment) {
    return t('invitations.modal.collab.wizard.send.compensationFixed', {
      amount: formatCents(
        compensation.fixedPayment.amountCents,
        compensation.fixedPayment.currency,
        language
      ),
    });
  }
  if (compensation.type === 'gifted_product' && compensation.giftedProduct) {
    const gifted = compensation.giftedProduct;
    const choice = briefGiftChoiceLabel(gifted, language);
    if (choice) return choice;
    const title =
      (gifted.catalogProducts?.[0] ?? gifted.catalog)?.title ?? gifted.manual?.name ?? '';
    return t('invitations.modal.collab.wizard.send.compensationGifted', { product: title });
  }
  return t('collabs.briefTemplates.sections.compensation.noneNote');
};
